/**
 * Maintenance report — `GET reports/maintenance/`, manager roles only. All
 * fields are plain JSON numbers/strings (a hand-written `APIView`, not a
 * `ModelSerializer`). No chart library in this project; rendered as plain
 * tables.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { getMaintenanceReport } from '../../api/endpoints/reports';

export function MaintenanceReportPage(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-maintenance'],
    queryFn: () => getMaintenanceReport(),
  });

  /** Client-side PDF, same shared chrome module `InvoiceDetailPage.tsx`'s "Print invoice" uses — no server-side PDF endpoint. */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const report = query.data;
      if (report === undefined) return;

      const { jsPDF } = await import('jspdf');
      const { createBrandedDocument, drawTable, downloadPdf } = await import('../../lib/pdf');

      const { doc, contentStartY } = createBrandedDocument(jsPDF, 'MAINTENANCE REPORT');
      let y = contentStartY;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`Total requests: ${report.total_requests}`, 15, y);
      y += 6;
      doc.text(`Completed: ${report.completed}`, 15, y);
      y += 10;

      function section(title: string, head: string[], rows: (string | number)[][]): void {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(title, 15, y);
        y += 4;
        y = drawTable(doc, { startY: y, head, rows });
        y += 10;
      }

      section(
        'By status',
        ['Status', 'Count'],
        report.by_status.map((row) => [row.status, row.count]),
      );
      section(
        'By category',
        ['Category', 'Count'],
        report.by_category.map((row) => [row.category, row.count]),
      );
      section(
        'By priority',
        ['Priority', 'Count'],
        report.by_priority.map((row) => [row.priority, row.count]),
      );
      section(
        'Monthly trend',
        ['Month', 'Count'],
        report.monthly_trend.map((row) => [row.month, row.count]),
      );

      downloadPdf(doc, 'maintenance-report.pdf');
    },
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const report = query.data;

  return (
    <section>
      <h2>Maintenance report</h2>

      <p>
        <button type="button" disabled={exportPdf.isPending} onClick={() => exportPdf.mutate()}>
          {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
        </button>
      </p>

      {exportPdf.isError && (
        <p role="alert" className="form__error">
          {parseApiError(exportPdf.error).message}
        </p>
      )}

      <dl>
        <dt>Total requests</dt>
        <dd>{report.total_requests}</dd>
        <dt>Completed</dt>
        <dd>{report.completed}</dd>
      </dl>

      <h3>By status</h3>
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {report.by_status.map((row) => (
            <tr key={row.status}>
              <td>{row.status}</td>
              <td>{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>By category</h3>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {report.by_category.map((row) => (
            <tr key={row.category}>
              <td>{row.category}</td>
              <td>{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>By priority</h3>
      <table>
        <thead>
          <tr>
            <th>Priority</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {report.by_priority.map((row) => (
            <tr key={row.priority}>
              <td>{row.priority}</td>
              <td>{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Monthly trend</h3>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {report.monthly_trend.map((row) => (
            <tr key={row.month}>
              <td>{row.month}</td>
              <td>{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default MaintenanceReportPage;
